import {
  AddIcon,
  Alert,
  Button,
  CircularProgress,
  Stack,
} from '@all-manager/design-system-v2';
import { NoItems } from '../../../../components/NoItems/NoItems';
import NoDocumentsIcon from '../../../documents/assets/no-documents.svg?react';
import AddQuoteReceivedActionModal from '../actionModales/AddQuoteReceivedActionModal/AddQuoteReceivedActionModal';
import AcceptQuoteModal from './AcceptQuoteModal/AcceptQuoteModal';
import DeleteQuoteModal from './DeleteQuoteModal/DeleteQuoteModal';
import QuoteCard from './QuoteCard/QuoteCard';
import ResetQuoteModal from './ResetQuoteModal/ResetQuoteModal';
import { useQuotesTab } from './useQuotesTab';

type Props = {
  subTopicId: string;
};

export default function QuotesTab(props: Props) {
  const {
    isAdmin,
    quotes,
    totalCount,
    isLoading,
    error,
    isNewQuoteModalOpen,
    quoteToAccept,
    quoteToEdit,
    quoteToDelete,
    quoteToReset,
    itemToEdit,
    handleAccept,
    handleRefuse,
    handleResetToPending,
    handleAttachSignedDocument,
    handleEdit,
    handleDelete,
    handleOpenNewQuoteModal,
    handleCloseNewQuoteModal,
    handleCloseAcceptModal,
    handleCloseEditModal,
    handleCloseDeleteModal,
    handleCloseResetModal,
  } = useQuotesTab(props);

  if (isLoading) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Une erreur est survenue lors du chargement des devis.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Top bar: New Quote button */}
      {isAdmin ? (
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={handleOpenNewQuoteModal}
          >
            Nouveau devis
          </Button>
        </Stack>
      ) : null}

      {/* List of full-width neutral cards */}
      {totalCount === 0 ? (
        <NoItems
          title="Aucun devis"
          subtitle="Ajoutez des devis pour comparer et gérer les bons pour accord."
          image={<NoDocumentsIcon />}
        />
      ) : (
        <Stack spacing={2}>
          {quotes.map((quote) => (
            <QuoteCard
              key={quote.id}
              quote={quote}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAccept={handleAccept}
              onRefuse={handleRefuse}
              onResetToPending={handleResetToPending}
              onAttachSignedDocument={handleAttachSignedDocument}
            />
          ))}
        </Stack>
      )}

      {/* New quote modal */}
      {isNewQuoteModalOpen ? (
        <AddQuoteReceivedActionModal
          onClose={handleCloseNewQuoteModal}
          subjectId={props.subTopicId}
        />
      ) : null}

      {/* Edit quote modal */}
      {quoteToEdit ? (
        <AddQuoteReceivedActionModal
          onClose={handleCloseEditModal}
          subjectId={props.subTopicId}
          item={itemToEdit}
        />
      ) : null}

      {/* Accept / BPA modal */}
      {quoteToAccept ? (
        <AcceptQuoteModal
          quote={quoteToAccept}
          subjectId={props.subTopicId}
          onClose={handleCloseAcceptModal}
        />
      ) : null}

      {/* Delete confirmation modal */}
      {quoteToDelete ? (
        <DeleteQuoteModal
          quote={quoteToDelete}
          subjectId={props.subTopicId}
          onClose={handleCloseDeleteModal}
        />
      ) : null}

      {/* Reset to pending with document confirmation modal */}
      {quoteToReset ? (
        <ResetQuoteModal
          quote={quoteToReset}
          subjectId={props.subTopicId}
          onClose={handleCloseResetModal}
        />
      ) : null}
    </Stack>
  );
}
